import React, { useState } from 'react';
import { ClipPrompt } from '../../../api/structs/clip';
import { MediaLibraryItem } from '../../../api/media';
import API from '../../../api/api';
import { constructMediaUrl } from '../../../api/helpers';
import { Button } from '../../ui';
import MediaLibraryPickerModal from '../../modals/MediaLibraryPickerModal';
import { isMusicCandidate } from '../../clip-editor/audioResolution';
import { useToast } from '../../../hooks/useToast';

interface MusicTrackProps {
  clipId: string;
  clip: ClipPrompt;
  onRefresh: () => void;
}

/**
 * The bound track, else an audio item that is (or can only be) the music.
 *
 * The fallback used to be "any audio item that is not narration", which made a
 * leftover narration take reappear the moment the binding was cleared, so the
 * lane could never be emptied. It is now the render path's own rule
 * (`isMusicCandidate`, mirroring `models.IsMusicCandidate`): an item that says
 * 'music', else an unroled audio item — takes that predate the role stamp are
 * still the clip's music — and never the take the clip binds as its narration,
 * which is what a pre-role narration take is: no role to exclude it by, yet
 * `narrator_media_id` names it. Among the candidates the roled music still
 * wins over an unroled take.
 */
const getMusicItem = (clip: ClipPrompt) => {
  const musicId = clip.metadata?.music_media_id || clip.metadata?.musicMediaId;
  if (musicId) {
    return clip.media?.audios?.find((a) => a.id === musicId) || null;
  }
  const narratorBoundId = String(
    clip.metadata?.narrator_media_id || clip.metadata?.narratorMediaId || ''
  ).trim();
  const audios = (clip.media?.audios || []).filter((a) => isMusicCandidate(a, narratorBoundId));
  return (
    audios.find((a) => a.metadata?.role === 'music') ||
    audios.find((a) => !a.metadata?.role && a.type === 'audio') ||
    null
  );
};

const MusicTrack: React.FC<MusicTrackProps> = ({ clipId, clip, onRefresh }) => {
  const toast = useToast();
  const [showPicker, setShowPicker] = useState(false);
  const musicItem = getMusicItem(clip);

  const handleSelect = async (item: MediaLibraryItem) => {
    try {
      await API.editClipMetadata(clipId, 'music_media_id', item.id);
      onRefresh();
    } catch (error: any) {
      toast({ text: `Failed to set music: ${error.message}`, level: 'error' });
    }
  };

  // One backend call: it detaches the resolved audio item AND clears the key,
  // so nothing is left on the clip for the render path to auto-wire back in.
  const handleRemove = async () => {
    if (!musicItem) return;
    try {
      await API.deleteClipAudio(clipId, 'music');
      onRefresh();
    } catch (error: any) {
      toast({ text: `Failed to remove music: ${error.message}`, level: 'error' });
    }
  };

  const audioUrl = musicItem?.file_url ? constructMediaUrl(musicItem.file_url) : null;

  return (
    <>
      <div className="border border-white/10 bg-black px-4 py-3">
        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2">Music</p>

        {musicItem && audioUrl ? (
          <div className="flex items-center gap-3">
            <audio controls preload="none" src={audioUrl} className="flex-1 h-8 min-w-0" />
            <Button variant="ghost" size="sm" onClick={() => setShowPicker(true)}>Replace</Button>
            <Button variant="ghost" size="sm" onClick={handleRemove} aria-label="Remove music">✕</Button>
          </div>
        ) : (
          <button
            onClick={() => setShowPicker(true)}
            className="text-xs text-zinc-600 hover:text-zinc-300 transition-colors font-mono"
          >
            no music — click to add from library
          </button>
        )}
      </div>

      <MediaLibraryPickerModal
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        filterType="audio"
        onSelect={handleSelect}
      />
    </>
  );
};

export default MusicTrack;
